import { Box } from '@mui/material';
import type { ContractType } from '@timelog/pm-shared';
import { useTranslation } from 'react-i18next';

import { contractForecast } from './contractStaffing';
import type { ContractStaffing } from './contractStaffing';

import type { BudgetMetric } from '@/components/financials/metricReading';
import { factorTrend, formatFactorAs } from '@/components/financials/metricReading';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { FigureTooltip, KPI_BOX_SX, KPI_LABEL_SX, KPI_VALUE_SX } from '@/components/ui';
import { formatAmount, formatForecastAmount, formatHours } from '@/lib';

/**
 * The four contract KPIs (2026-10-29, the designer's ask) under the task
 * panel's Contract insight and, per contract, in the rail's Contract insights,
 * in the Financials aside's KPI-box design (`ui/kpiBox.ts`) so every surface
 * reads alike. In reading order:
 *
 * 1. **Forecasted factor** and 2. **Forecast** (the revenue) — by billing
 *    type (`contractForecast`). Fixed price: revenue = the staffing lines' cost
 *    on the mapped tasks (CALC-21) × the Own Work factor SET on the contract
 *    (CALC-20), factor = the Own Work amount ÷ that cost. Time & material and
 *    retainer: revenue = Σ the lines at their sell rates (CALC-2), factor =
 *    that revenue ÷ the cost. Two decimals (`formatFactorAs` — the margin
 *    reading when the budget model says so, as everywhere).
 * 3. **Allocated** — the aside's allocated-hours box: the share of the contract's
 *    Own Work hours somebody is allocated to (CALC-22 over CALC-11), with the
 *    hours under — or over, in the error tone — the contract beside it. Both
 *    sub-lines name the contract (2026-11-02, the designer's ask: "under" alone
 *    did not say under what), so they are this namespace's keys, not the aside's.
 * 4. **Planned** (coverage) — the share of the allocated hours planned over
 *    time. Always 0% for now: the prototype plans allocations as totals, not
 *    over days (the ResourceAxis bookings are a separate capability, ADR 0021);
 *    its tooltip says so. The rail leaves it out (`showPlanned`, 2026-10-29,
 *    the designer's ask), and since 3 Nov the task panel does too, for now,
 *    so the two lay out alike (Mette's note). No sub-lines on any box
 *    (2026-10-29): the figures explain themselves on hover, the way the
 *    aside's do.
 *
 * Considered and not reused: `InsightTile` (sunken ground, not the aside's
 * white hairline box) and the ring-shaped `FigureBlock` (no box at
 * all; removed 3 Nov). The aside's boxes are inline JSX there,
 * so the three styles moved to `ui/kpiBox.ts` and both read them.
 */
export type ContractKpiBoxesProps = {
  staffing: ContractStaffing;
  /** The mapped contract's billing type — decides how the forecast is read. */
  contractType: ContractType;
  /** The project's currency code, shown beside the forecast figure. */
  currency: string;
  /** The Own Work factor set on the contract; null when the task maps none. */
  ownFactor: number | null;
  metric: BudgetMetric;
  /** Whether the Planned box is shown after Allocated — on by default; both surfaces turn it off for now. */
  showPlanned?: boolean;
  /**
   * Whether the Allocated box is shown — on by default. The task panel turns it
   * off (3 Nov, Mette): its contract hours bar says the allocated share there.
   */
  showAllocated?: boolean;
};

/** The hint that stands in for a figure the data cannot give yet. */
const KPI_HINT_SX = { fontSize: 10, color: 'var(--fg-3)', lineHeight: 1.35 } as const;

export const ContractKpiBoxes = ({
  staffing,
  contractType,
  currency,
  ownFactor,
  metric,
  showPlanned = true,
  showAllocated = true,
}: ContractKpiBoxesProps) => {
  const { t } = useTranslation(['pmPlanningPanel', 'pmCommon', 'pmFinancials']);
  const hasCost = staffing.allocatedCost > 0;
  const fixed = contractType === 'fixed';
  const forecast = contractForecast(staffing, contractType, ownFactor);
  // The arrow the aside's budgeted factor wears against its target — here the
  // forecast against the Own Work factor set on the contract.
  const trend = factorTrend(forecast.factor, ownFactor);
  const overAllocated = staffing.allocatedPct !== null && staffing.allocatedPct > 100;
  // A fixed-price forecasted factor needs an Own Work amount before anything
  // else (2026-11-02, the designer's ask): without one the box hints at the
  // budget, not at the staffing.
  const missingOwnAmount = fixed && staffing.ownAmount <= 0;

  return (
    <Box
      data-automation-id="contract-kpis"
      sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: '14px' }}
    >
      {/* Dashed, like the aside's forecast box: a projection, not a budgeted figure. */}
      <FigureTooltip
        title={
          fixed
            ? metric === 'factor'
              ? t('help.ForecastFactor')
              : t('help.ForecastMargin')
            : t('help.TmForecastFactor')
        }
      >
        <Box
          sx={{ ...KPI_BOX_SX, borderStyle: 'dashed' }}
          tabIndex={0}
          data-automation-id="contract-kpi-forecast-factor"
          data-contract-type={contractType}
        >
          <Box sx={KPI_LABEL_SX}>
            {t(
              metric === 'factor'
                ? 'pmCommon:term.ForecastedFactor'
                : 'pmCommon:term.ForecastedMargin',
            )}
          </Box>
          {forecast.factor !== null ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box component="span" sx={KPI_VALUE_SX}>
                {formatFactorAs(metric, forecast.factor)}
              </Box>
              <Box
                component="span"
                data-automation-id="contract-kpi-forecast-factor-trend"
                data-trend={trend.icon}
                sx={{ display: 'inline-flex' }}
              >
                <MaterialSymbol name={trend.icon} size={18} color={trend.color} />
              </Box>
            </Box>
          ) : (
            <Box
              data-automation-id="contract-kpi-forecast-factor-hint"
              data-missing={missingOwnAmount ? 'own-amount' : 'resources'}
              sx={KPI_HINT_SX}
            >
              {missingOwnAmount
                ? t('contractStaffing.NoOwnAmountHint')
                : t('pmFinancials:aside.ForecastHint')}
            </Box>
          )}
        </Box>
      </FigureTooltip>

      <FigureTooltip
        title={
          fixed ? t('help.ContractForecastedRevenueAtFactor') : t('help.ContractForecastedRevenue')
        }
      >
        <Box
          sx={{ ...KPI_BOX_SX, borderStyle: 'dashed' }}
          tabIndex={0}
          data-automation-id="contract-kpi-forecast-revenue"
          data-contract-type={contractType}
        >
          <Box sx={KPI_LABEL_SX}>{t('contractStaffing.Forecast')}</Box>
          {hasCost && forecast.revenue !== null ? (
            // The figure, and beside it how far it sits from the contract's Own
            // Work amount — under in the muted tone, over in the error tone —
            // laid out like the allocated box's remaining hours.
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, flexWrap: 'wrap' }}>
              <Box
                component="span"
                data-automation-id="contract-kpi-forecast-revenue-value"
                sx={KPI_VALUE_SX}
              >
                {formatForecastAmount(forecast.revenue)}
              </Box>
              {/* The currency code beside the figure, as the aside's cost box shows it. */}
              <Box
                component="span"
                data-automation-id="contract-kpi-forecast-revenue-currency"
                sx={{ fontSize: 11, color: 'var(--fg-3)' }}
              >
                {currency}
              </Box>
              <Box
                component="span"
                data-automation-id="contract-kpi-forecast-revenue-vs-own"
                sx={{
                  fontSize: 11,
                  marginLeft: 'auto',
                  textAlign: 'right',
                  color:
                    forecast.revenue > staffing.ownAmount ? 'var(--color-error-80)' : 'var(--fg-3)',
                }}
              >
                {forecast.revenue > staffing.ownAmount
                  ? t('contractStaffing.ForecastOver', {
                      amount: formatAmount(forecast.revenue - staffing.ownAmount),
                    })
                  : t('contractStaffing.ForecastUnder', {
                      amount: formatAmount(staffing.ownAmount - forecast.revenue),
                    })}
              </Box>
            </Box>
          ) : (
            <Box data-automation-id="contract-kpi-forecast-revenue-hint" sx={KPI_HINT_SX}>
              {t('pmFinancials:aside.ForecastHint')}
            </Box>
          )}
        </Box>
      </FigureTooltip>

      {showAllocated ? (
        <FigureTooltip title={t('pmFinancials:help.AllocatedHours')}>
          <Box sx={KPI_BOX_SX} tabIndex={0} data-automation-id="contract-kpi-allocated-hours">
            <Box sx={KPI_LABEL_SX}>{t('pmCommon:term.Allocated')}</Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, flexWrap: 'wrap' }}>
              <Box
                component="span"
                data-automation-id="contract-kpi-allocated-hours-pct"
                sx={{
                  ...KPI_VALUE_SX,
                  color: overAllocated ? 'var(--color-error-80)' : 'var(--fg-1)',
                }}
              >
                {staffing.allocatedPct === null
                  ? '—'
                  : `${String(Math.round(staffing.allocatedPct))}%`}
              </Box>
              <Box
                component="span"
                data-automation-id="contract-kpi-allocated-hours-remaining"
                sx={{
                  fontSize: 11,
                  // Right-aligned in the box, on the figure's line when it fits.
                  marginLeft: 'auto',
                  textAlign: 'right',
                  color: staffing.allocatedRemaining < 0 ? 'var(--color-error-80)' : 'var(--fg-3)',
                }}
              >
                {staffing.allocatedRemaining >= 0
                  ? t('contractStaffing.HoursUnderContract', {
                      hours: formatHours(staffing.allocatedRemaining),
                    })
                  : t('contractStaffing.HoursOverContract', {
                      hours: formatHours(-staffing.allocatedRemaining),
                    })}
              </Box>
            </Box>
          </Box>
        </FigureTooltip>
      ) : null}

      {/* Always 0%: nothing in the prototype plans allocated hours over time yet. */}
      {showPlanned ? (
        <FigureTooltip title={t('help.PlannedCoverage')}>
          <Box
            sx={{ ...KPI_BOX_SX, borderStyle: 'dashed' }}
            tabIndex={0}
            data-automation-id="contract-kpi-planned"
            data-unavailable="true"
          >
            <Box sx={KPI_LABEL_SX}>{t('contractStaffing.Planned')}</Box>
            <Box component="span" sx={{ ...KPI_VALUE_SX, color: 'var(--fg-3)' }}>
              0%
            </Box>
          </Box>
        </FigureTooltip>
      ) : null}
    </Box>
  );
};
